import { get } from "lodash";
import moment from "moment-timezone";
import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import { Segment, Header, Divider, Loader } from "semantic-ui-react";

import { Media } from "../../styles/Media.js";

import ISOWeekDateRange from "../forms/time/ISOWeekDateRange.jsx";
import UsageGrid from "./UsageGrid.jsx";
import PunchChart from "./punchChart/PunchChart.jsx";

import { MODIFIED_AT_TIME_FORMAT } from "../config.js";

import entityActions from "../../state/reducers/entity/entityActions.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class UsagePanel extends PureComponent {
  static propTypes = {
    data: PropTypes.object,
    disabled: PropTypes.bool.isRequired,

    chargeIsFetching: PropTypes.bool.isRequired,
    chargeIsErrored: PropTypes.bool.isRequired,
    usageIsFetching: PropTypes.bool.isRequired,
    usageIsErrored: PropTypes.bool.isRequired,
    measurementsIsFetching: PropTypes.bool.isRequired,
    measurementsIsErrored: PropTypes.bool.isRequired,
    copy: PropTypes.func.isRequired
  };

  static defaultProps = {
    disabled: false
  };

  // -- HANDLERS -- //

  handleDateChange = (from, to) => {
    const { data, getUsage, getCharge, getMeasurements } = this.props;

    if (data.charge) {
      var id = data.charge["@id"];

      getCharge(
        // @todo @id on Abacus endpoints are now (huge API platform upgrade in '25) different
        // for now we manipulate the ID in the SPA until we can address this more structurally
        id.endsWith("/charge") ? id : `${id}/charge`,
        from.toISOString(),
        to.toISOString()
      );
    }

    if (data.usage) {
      var id = data.usage["@id"];

      getUsage(
        // @todo @id on Abacus endpoints are now (huge API platform upgrade in '25) different
        // for now we manipulate the ID in the SPA until we can address this more structurally
        id.endsWith("/usage") ? id : `${id}/usage`,
        from.toISOString(),
        to.toISOString()
      );
    }

    if (data.measurements) {
      var id = data.measurements["@id"];

      getMeasurements(
        // @todo @id on Abacus endpoints are now (huge API platform upgrade in '25) different
        // for now we manipulate the ID in the SPA until we can address this more structurally
        id.endsWith("/measurements") ? id : `${id}/measurements`,
        from.toISOString(),
        to.toISOString()
      );
    }
  };

  // -- RENDER -- //

  renderHumanReadableWeek = () => {
    const { chargeIsErrored, usageIsErrored, data, copy } = this.props;

    var fromKey;

    if (fromKey === undefined && data.hasOwnProperty("charge")) {
      fromKey = "charge.charge.from";
    }

    if (fromKey === undefined && data.hasOwnProperty("usage")) {
      fromKey = "usage.usage.from";
    }

    const from = get(data, fromKey, null);

    if (chargeIsErrored || usageIsErrored || !from) {
      return null;
    }

    const timeObj = moment(from).utc();
    return `${copy("usage.usage_panel.week_starting")} ${timeObj.format(
      `${MODIFIED_AT_TIME_FORMAT} (z)`
    )}`;
  };

  renderUsage = () => {
    const { chargeIsErrored, data, copy } = this.props;

    if (chargeIsErrored) {
      return <p>{copy("usage.usage_panel.usage_is_errored")}</p>;
    }

    const chargeData = get(data, "charge.charge", null);
    const usageData = get(data, "usage.usage", null);

    if (!chargeData) {
      return <p>{copy("usage.usage_panel.usage_unavailable")}</p>;
    }

    if (typeof chargeData !== "object") {
      return null;
    }
    return <UsageGrid chargeData={chargeData} usageData={usageData} />;
  };

  renderMeasurements = () => {
    const { measurementsIsErrored, data, copy } = this.props;

    if (measurementsIsErrored) {
      return <p>{copy("usage.usage_panel.measurements_is_errored")}</p>;
    }

    const measurementsData = get(data, "measurements.measurements", null);

    if (!measurementsData || !Array.isArray(measurementsData)) {
      return <p>{copy("usage.usage_panel.measurements_unavailable")}</p>;
    }

    return (
      <PunchChart
        data={measurementsData}
        copy={copy("usage.usage_panel.punch_chart")}
      />
    );
  };

  render() {
    const {
      data,
      usageIsFetching,
      chargeIsFetching,
      measurementsIsFetching,
      disabled,
      copy
    } = this.props;

    // display loader where required
    if (usageIsFetching || chargeIsFetching || measurementsIsFetching) {
      return (
        <Segment secondary>
          <Header as="h3">{copy("usage.usage_panel.usage_title")}</Header>
          <Loader active inline size="small" />
        </Segment>
      );
    }

    // bail early if no data of any kind is available
    if (!data) {
      return (
        <Segment secondary>
          <Header as="h3">{copy("usage.usage_panel.usage_title")}</Header>
          <p>{copy("usage.usage_panel.unavailable")}</p>
        </Segment>
      );
    }

    // lack of property indicates lack of permission to view
    if (
      !data.hasOwnProperty("charge") &&
      !data.hasOwnProperty("usage") &&
      !data.hasOwnProperty("measurements")
    ) {
      return null;
    }

    var fromKey;
    var toKey;

    if (fromKey === undefined && data.hasOwnProperty("charge")) {
      fromKey = "charge.charge.from";
    }

    if (toKey === undefined && data.hasOwnProperty("charge")) {
      toKey = "charge.charge.to";
    }

    if (fromKey === undefined && data.hasOwnProperty("usage")) {
      fromKey = "usage.usage.from";
    }

    if (toKey === undefined && data.hasOwnProperty("usage")) {
      toKey = "usage.usage.to";
    }

    return (
      <Segment secondary disabled={disabled}>
        <ISOWeekDateRange
          from={get(data, fromKey, null)}
          to={get(data, toKey, null)}
          min={data.createdAt}
          max={data.destroyedAt}
          disabled={disabled}
          onChange={this.handleDateChange}
        />
        {(data.usage || data.charge) && (
          <Fragment>
            <Divider />
            <p>
              <strong>{this.renderHumanReadableWeek()}</strong>
            </p>
            <Divider />
            <Header as="h3">{copy("usage.usage_panel.usage_title")}</Header>
            {this.renderUsage()}
          </Fragment>
        )}
        {data.measurements && (
          <Media greaterThanOrEqual="tablet">
            <Divider />
            <Header as="h3">
              {copy("usage.usage_panel.measurements_title")}
            </Header>
            <p>{copy("usage.usage_panel.measurements_note")}</p>
            {this.renderMeasurements()}
          </Media>
        )}
      </Segment>
    );
  }
}

const mapStateToProps = state => ({
  chargeIsFetching: state.charge.isFetching,
  chargeIsErrored: state.charge.isErrored,
  usageIsFetching: state.usage.isFetching,
  usageIsErrored: state.usage.isErrored,
  measurementsIsFetching: state.measurements.isFetching,
  measurementsIsErrored: state.measurements.isErrored,
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getCharge: (id, from, to) => dispatch(entityActions.getCharge(id, from, to)),
  getUsage: (id, from, to) => dispatch(entityActions.getUsage(id, from, to)),
  getMeasurements: (id, from, to) =>
    dispatch(entityActions.getMeasurements(id, from, to))
});

export default connect(mapStateToProps, mapDispatchToProps)(UsagePanel);
